import { type BarChartTooltipType, Box, Card, Text } from '@factorialco/deprecated-design-system'
import { sum } from 'lodash'

import LegendItem from 'modules/core/components/BarChart/LegendItem'

import { EmployeePulseIcon } from 'modules/engagement/components/EmployeePulseIcon'
import { getEmployeeMoodToolTip } from 'modules/engagement/components/EmployeePulseIcon'
import type { ChartTooltipProps } from 'modules/performance_analytics/components/EmployeePulseInsights/types'

interface TooltipData {
  readonly label: string
  readonly value: number
}

interface ProcessedTooltipData {
  readonly dataArray: readonly TooltipData[]
  readonly total: number
  readonly hasGrouping: boolean
}

// Extract numeric value from mood label for icon rendering
const extractNumericValueFromMoodLabel = (label: string): number => {
  for (let i = 1; i <= 5; i++) {
    if (getEmployeeMoodToolTip(i) === label) {
      return i
    }
  }
  return 1
}

const processTooltipData = (
  chartData: ChartTooltipProps['chartData'],
  label: string
): ProcessedTooltipData => {
  const bucketData = chartData.find((bucket) => bucket.bucket === label)
  if (!bucketData) {
    return {
      dataArray: [],
      total: 0,
      hasGrouping: false,
    }
  }

  const dataEntries = Object.entries(bucketData).filter(([key]) => key !== 'bucket')
  const dataArray: TooltipData[] = dataEntries.map(([key, value]) => ({
    label: key,
    value: typeof value === 'number' ? value : 0,
  }))

  const total = sum(dataArray.map((item) => item.value))
  const hasGrouping =
    dataArray.length > 1 || (dataArray.length === 1 && dataArray[0]?.label !== 'score')

  return {
    dataArray,
    total,
    hasGrouping,
  }
}

const MoodTooltipContent = ({ label, value }: { label: string; value: number }) => {
  // Extract numeric value from the mood label
  const numericValue = extractNumericValueFromMoodLabel(label)

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <EmployeePulseIcon mood={numericValue} />
        <Text whiteSpace="nowrap" size="100">
          {label}
        </Text>
      </Box>
      <Text whiteSpace="nowrap" size="100" weight="bold">
        {value}
      </Text>
    </Box>
  )
}

const GroupedTooltipContent = ({
  processedData,
  keyColorMap,
}: {
  processedData: ProcessedTooltipData
  keyColorMap: ChartTooltipProps['keyColorMap']
}) => (
  <Box flexDirection="column" gap="s8">
    {processedData.dataArray.map((item) => (
      <LegendItem key={item.label} color={keyColorMap?.[item.label] ?? 'viridian400'} gap="s8">
        <Text whiteSpace="nowrap" size="100" weight="bold">
          {item.label}: {item.value}
        </Text>
      </LegendItem>
    ))}
  </Box>
)

export const ChartTooltip = ({ chartData, groupBy, keyColorMap }: ChartTooltipProps) => {
  const Tooltip = ({ label, value }: BarChartTooltipType) => {
    const processedData = processTooltipData(chartData, String(label))
    const isGrouped = Boolean(groupBy) && processedData.hasGrouping

    return (
      <Box
        style={{
          zIndex: '1000',
          position: 'relative',
        }}
      >
        <Card>
          <Card.Content>
            {isGrouped ? (
              <GroupedTooltipContent processedData={processedData} keyColorMap={keyColorMap} />
            ) : (
              <MoodTooltipContent label={String(label)} value={Number(value)} />
            )}
          </Card.Content>
        </Card>
      </Box>
    )
  }

  return Tooltip
}
